import moment from 'moment';

const DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Returns true when the inclusive [startDate, endDate] range contains at least
 * one occurrence of the given dayOfWeek. Returns true also for invalid/missing
 * inputs and for short-circuit cases (range >= 6 calendar days), since any
 * 6-day range will always include any weekday.
 *
 * Notes on contract:
 * - Returning true on invalid input keeps this helper safe for use inside a
 *   react-hook-form validate function: invalid scenarios are handled by other
 *   rules (required, "End >= Start"), so we should never produce a misleading
 *   "weekday not included" error in those cases.
 * - dayOfWeek is compared via String(dayOfWeek) to be tolerant of numeric and
 *   string forms (the singleSelect option uses string values like '0'..'6').
 *
 * @param {string} startDate MM/DD/YYYY
 * @param {string} endDate   MM/DD/YYYY
 * @param {string|number} dayOfWeek 0..6 (0 = Sunday)
 * @returns {boolean}
 */
export const rangeIncludesDayOfWeek = (startDate, endDate, dayOfWeek) => {
  if (!startDate || !endDate) return true;
  if (dayOfWeek === undefined || dayOfWeek === null || dayOfWeek === '') {
    return true;
  }

  const targetDow = Number(dayOfWeek);
  if (Number.isNaN(targetDow) || targetDow < 0 || targetDow > 6) {
    return true;
  }

  const start = moment(startDate, DATE_FORMAT, true);
  const end = moment(endDate, DATE_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return true;
  if (end.isBefore(start)) return true;

  const diffDays = end.diff(start, 'days');
  // A 6-day range (i.e. 7 calendar days inclusive) always covers all weekdays.
  if (diffDays >= 6) return true;

  const cursor = start.clone();
  while (!cursor.isAfter(end, 'day')) {
    if (cursor.day() === targetDow) return true;
    cursor.add(1, 'day');
  }
  return false;
};

/**
 * Returns true when the inclusive [startDate, endDate] range contains at least
 * one occurrence of the given dayOfMonth. Returns true also for invalid/missing
 * inputs and for short-circuit cases (range >= 31 calendar days), since any
 * 31-day window must contain every legal day-of-month value (including the
 * "last day" sentinel).
 *
 * The dayOfMonth value matches the form's DAY_OF_MONTH_OPTIONS:
 *   - '1'..'28' (string) for explicit days, OR
 *   - '-1' for "last day of month".
 *
 * For '-1' we iterate day-by-day and return true the first time the cursor
 * lands on the natural last day of its month (handles Feb 28/29, 30/31 months).
 *
 * Legacy data note: the create-modal select only exposes 1..28 plus -1, but
 * legacy schedules persisted in the database may carry dayOfMonth values of
 * 29, 30 or 31. To avoid blocking users from editing those legacy schedules
 * with a misleading inline error, we deliberately treat any value outside
 * [1..28] (other than -1) as "no extra rule to enforce" and return true. This
 * is intentional -- the cross-field warning is a UX safeguard for newly
 * authored schedules, not a hard data contract.
 *
 * @param {string} startDate MM/DD/YYYY
 * @param {string} endDate   MM/DD/YYYY
 * @param {string|number} dayOfMonth 1..28 or -1 (legacy 29..31 returns true)
 * @returns {boolean}
 */
export const rangeIncludesDayOfMonth = (startDate, endDate, dayOfMonth) => {
  if (!startDate || !endDate) return true;
  if (dayOfMonth === undefined || dayOfMonth === null || dayOfMonth === '') {
    return true;
  }

  const targetDom = Number(dayOfMonth);
  if (Number.isNaN(targetDom)) return true;
  if (targetDom !== -1 && (targetDom < 1 || targetDom > 28)) return true;

  const start = moment(startDate, DATE_FORMAT, true);
  const end = moment(endDate, DATE_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return true;
  if (end.isBefore(start)) return true;

  const diffDays = end.diff(start, 'days');
  // 30 days difference == 31 calendar days inclusive: spans an entire month.
  if (diffDays >= 30) return true;

  const cursor = start.clone();
  while (!cursor.isAfter(end, 'day')) {
    if (targetDom === -1) {
      if (cursor.date() === cursor.daysInMonth()) return true;
    } else if (cursor.date() === targetDom) {
      return true;
    }
    cursor.add(1, 'day');
  }
  return false;
};

/**
 * Returns true when the [startDate, endDate] range allows at least one
 * complete recurring cycle. The minimum sufficient condition is endDate
 * strictly greater than startDate, because AWS EventBridge Scheduler rejects
 * recurring schedules where StartDate >= EndDate ("ValidationException -
 * The StartDate you specify must come before the EndDate.").
 *
 * Returns true also for missing/invalid inputs to keep this helper safe for
 * use inside a react-hook-form validate function: the "End >= Start" rule
 * and the required-field rule already cover those scenarios upstream.
 *
 * Intended for weekly/monthly frequencies only. Daily/one-time do not call this.
 *
 * @param {string} startDate MM/DD/YYYY
 * @param {string} endDate   MM/DD/YYYY
 * @returns {boolean}
 */
export const rangeAllowsAtLeastOneCycle = (startDate, endDate) => {
  if (!startDate || !endDate) return true;
  const start = moment(startDate, DATE_FORMAT, true);
  const end = moment(endDate, DATE_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return true;
  // "End >= Start" rule already handles end < start with a different message.
  if (end.isBefore(start, 'day')) return true;
  return end.isAfter(start, 'day');
};

// Maximum days to find any dayOfWeek (0-6) starting from any day.
const MAX_WEEKDAY_LOOKAHEAD_DAYS = 7;
// Covers two consecutive months, sufficient for any dayOfMonth (1-31 or -1)
// including the worst case "dom=31 starting from a 28-day month".
const MAX_DAYOFMONTH_LOOKAHEAD_DAYS = 62;

/**
 * Returns the moment representing the FIRST real execution datetime of a
 * schedule based on its frequency and selected day-of-week/day-of-month.
 *
 * For weekly/monthly, the first execution is NOT necessarily startDate +
 * executionTime -- it is the first occurrence of dayOfWeek/dayOfMonth on or
 * after startDate at the given executionTime. For daily and one-time the
 * datetime is always startDate (or executionDate) + executionTime.
 *
 * Returns null when required inputs are missing or invalid; callers should
 * skip the time-based validation in that case (other validators cover the
 * missing-field scenarios).
 *
 * @param {object} params
 * @param {string} params.frequency 'one-time' | 'daily' | 'weekly' | 'monthly'
 * @param {string} [params.startDate] MM/DD/YYYY (used for daily/weekly/monthly)
 * @param {string} [params.executionDate] MM/DD/YYYY (used for one-time)
 * @param {string} params.executionTime24h 'HH:mm'
 * @param {string|number} [params.dayOfWeek] 0..6 (weekly only)
 * @param {string|number} [params.dayOfMonth] 1..31 or -1 for last day (monthly only)
 * @returns {moment.Moment|null}
 */
export const computeFirstExecutionDateTime = ({
  frequency,
  startDate,
  executionDate,
  executionTime24h,
  dayOfWeek,
  dayOfMonth,
}) => {
  if (!executionTime24h || typeof executionTime24h !== 'string') return null;
  const [hourStr, minuteStr] = executionTime24h.split(':');
  const hours = Number(hourStr);
  const minutes = Number(minuteStr);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const applyTime = (m) =>
    m.clone().hours(hours).minutes(minutes).seconds(0).milliseconds(0);

  if (frequency === 'one-time') {
    const base = moment(executionDate, DATE_FORMAT, true);
    if (!base.isValid()) return null;
    return applyTime(base);
  }

  if (frequency === 'daily') {
    const base = moment(startDate, DATE_FORMAT, true);
    if (!base.isValid()) return null;
    return applyTime(base);
  }

  if (frequency === 'weekly') {
    if (dayOfWeek === undefined || dayOfWeek === null || dayOfWeek === '') {
      return null;
    }
    const targetDow = Number(dayOfWeek);
    if (Number.isNaN(targetDow) || targetDow < 0 || targetDow > 6) return null;
    const cursor = moment(startDate, DATE_FORMAT, true).startOf('day');
    if (!cursor.isValid()) return null;
    for (let i = 0; i < MAX_WEEKDAY_LOOKAHEAD_DAYS; i += 1) {
      if (cursor.day() === targetDow) return applyTime(cursor);
      cursor.add(1, 'day');
    }
    return null;
  }

  if (frequency === 'monthly') {
    if (dayOfMonth === undefined || dayOfMonth === null || dayOfMonth === '') {
      return null;
    }
    const targetDom = Number(dayOfMonth);
    if (
      Number.isNaN(targetDom) ||
      (targetDom !== -1 && (targetDom < 1 || targetDom > 31))
    ) {
      return null;
    }
    const cursor = moment(startDate, DATE_FORMAT, true).startOf('day');
    if (!cursor.isValid()) return null;
    for (let i = 0; i < MAX_DAYOFMONTH_LOOKAHEAD_DAYS; i += 1) {
      const matches =
        targetDom === -1
          ? cursor.date() === cursor.daysInMonth()
          : cursor.date() === targetDom;
      if (matches) return applyTime(cursor);
      cursor.add(1, 'day');
    }
    return null;
  }

  return null;
};

/**
 * Returns the moment representing the NEXT real execution datetime of a
 * schedule as of `now`. Differs from `computeFirstExecutionDateTime` in two
 * ways that matter for the EDIT flow (ENG-27666):
 *
 *  1. The cursor never starts in the past. It is `max(startDate, startOfDay(now))`
 *     so the helper resolves to the next future occurrence regardless of how
 *     old the original startDate is.
 *  2. If the first matching occurrence falls on "today" but the executionTime
 *     has already passed, the helper rolls forward to the next valid cycle
 *     (next day for daily, next week for weekly, next month for monthly).
 *     This is exactly the EventBridge behavior for existing recurring schedules
 *     and is the correct value to compare against `now + 10 minutes` when the
 *     user only edits non-time fields (e.g. endDate=today).
 *
 * For one-time schedules this helper delegates to
 * `computeFirstExecutionDateTime` because there is no "next cycle" concept --
 * the single execution datetime is the value the user picked, full stop.
 *
 * Returns null for the same invalid/missing input cases as
 * `computeFirstExecutionDateTime`.
 *
 * @param {object} params
 * @param {string} params.frequency 'one-time' | 'daily' | 'weekly' | 'monthly'
 * @param {string} [params.startDate] MM/DD/YYYY (used for daily/weekly/monthly)
 * @param {string} [params.executionDate] MM/DD/YYYY (used for one-time)
 * @param {string} params.executionTime24h 'HH:mm'
 * @param {string|number} [params.dayOfWeek] 0..6 (weekly only)
 * @param {string|number} [params.dayOfMonth] 1..31 or -1 for last day (monthly only)
 * @param {moment.Moment} [params.now] injectable "current time" (defaults to moment())
 * @returns {moment.Moment|null}
 */
export const computeNextExecutionDateTimeFromNow = ({
  frequency,
  startDate,
  executionDate,
  executionTime24h,
  dayOfWeek,
  dayOfMonth,
  now = moment(),
}) => {
  if (!executionTime24h || typeof executionTime24h !== 'string') return null;
  const [hourStr, minuteStr] = executionTime24h.split(':');
  const hours = Number(hourStr);
  const minutes = Number(minuteStr);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  // One-time edits never roll forward -- there is no "next cycle". Defer to
  // the first-execution helper, which encodes the exact same contract.
  if (frequency === 'one-time') {
    return computeFirstExecutionDateTime({
      frequency,
      executionDate,
      executionTime24h,
    });
  }

  const applyTime = (m) =>
    m.clone().hours(hours).minutes(minutes).seconds(0).milliseconds(0);

  const todayStart = now.clone().startOf('day');
  // The "is candidate already in the past?" check below compares against the
  // current minute (seconds/millis floored). Otherwise, at e.g. 12:00:00.500
  // with executionTime=12:00, candidate=12:00:00.000 would be considered "in
  // the past" and the helper would roll forward by a full cycle, blocking the
  // UI 500ms after the schedule's minute actually started. Snapping to the
  // current minute gives a 1-minute tolerance that matches user expectations
  // and avoids spurious "in the past" failures within the same minute.
  const nowMinute = now.clone().startOf('minute');

  if (frequency === 'daily') {
    const start = moment(startDate, DATE_FORMAT, true);
    if (!start.isValid()) return null;
    // Cursor is the later of startDate and today (start-of-day). This handles
    // the EDIT case where startDate is far in the past.
    const cursor = start.isAfter(todayStart, 'day')
      ? start.clone().startOf('day')
      : todayStart.clone();
    const candidate = applyTime(cursor);
    if (candidate.isBefore(nowMinute)) {
      return applyTime(cursor.clone().add(1, 'day'));
    }
    return candidate;
  }

  if (frequency === 'weekly') {
    if (dayOfWeek === undefined || dayOfWeek === null || dayOfWeek === '') {
      return null;
    }
    const targetDow = Number(dayOfWeek);
    if (Number.isNaN(targetDow) || targetDow < 0 || targetDow > 6) return null;
    const start = moment(startDate, DATE_FORMAT, true);
    if (!start.isValid()) return null;
    const cursor = start.isAfter(todayStart, 'day')
      ? start.clone().startOf('day')
      : todayStart.clone();
    for (let i = 0; i < MAX_WEEKDAY_LOOKAHEAD_DAYS; i += 1) {
      if (cursor.day() === targetDow) {
        const candidate = applyTime(cursor);
        if (candidate.isBefore(nowMinute)) {
          // First match is "today" but execution time already passed -- roll
          // forward exactly one week.
          return applyTime(cursor.clone().add(7, 'days'));
        }
        return candidate;
      }
      cursor.add(1, 'day');
    }
    return null;
  }

  if (frequency === 'monthly') {
    if (dayOfMonth === undefined || dayOfMonth === null || dayOfMonth === '') {
      return null;
    }
    const targetDom = Number(dayOfMonth);
    if (
      Number.isNaN(targetDom) ||
      (targetDom !== -1 && (targetDom < 1 || targetDom > 31))
    ) {
      return null;
    }
    const start = moment(startDate, DATE_FORMAT, true);
    if (!start.isValid()) return null;
    const cursor = start.isAfter(todayStart, 'day')
      ? start.clone().startOf('day')
      : todayStart.clone();
    for (let i = 0; i < MAX_DAYOFMONTH_LOOKAHEAD_DAYS; i += 1) {
      const matches =
        targetDom === -1
          ? cursor.date() === cursor.daysInMonth()
          : cursor.date() === targetDom;
      if (matches) {
        const candidate = applyTime(cursor);
        if (candidate.isBefore(nowMinute)) {
          // First match is "today" but execution time already passed -- roll
          // forward one calendar month. We re-resolve the day-of-month from
          // the resulting month so dom=-1 (last day) and dom=31 cases land on
          // the correct day for the new month.
          const nextMonthStart = cursor
            .clone()
            .add(1, 'month')
            .startOf('month');
          for (let j = 0; j < MAX_DAYOFMONTH_LOOKAHEAD_DAYS; j += 1) {
            const nextMatches =
              targetDom === -1
                ? nextMonthStart.date() === nextMonthStart.daysInMonth()
                : nextMonthStart.date() === targetDom;
            if (nextMatches) return applyTime(nextMonthStart);
            nextMonthStart.add(1, 'day');
          }
          return null;
        }
        return candidate;
      }
      cursor.add(1, 'day');
    }
    return null;
  }

  return null;
};

/**
 * Returns true when a recurring schedule (daily/weekly/monthly) has at least
 * one valid execution inside the [startDate, endDate] window, computed
 * relative to NOW. The check accounts for facts that the generic
 * "End >= Start" rule and the `rangeIncludes*` helpers ignore:
 *
 *  1. Recurring schedules execute at a specific `executionTime` on each
 *     matching day. If the only matching day inside the window is "today" but
 *     today's executionTime has already passed (so the next execution rolls
 *     to the next cycle), then the window is empty. This is the ENG-27666
 *     T2 corner case for daily and the equivalent edge case for weekly and
 *     monthly schedules (e.g. weekly Tuesday with endDate=Tuesday-today and
 *     time already past; or monthly day=12 with endDate=05/12 and time past).
 *  2. EDIT mode must use `computeNextExecutionDateTimeFromNow` to discover the
 *     first execution because the persisted startDate is typically in the
 *     past. CREATE mode uses `computeFirstExecutionDateTime` so the validation
 *     reflects what the user just authored (startDate + executionTime).
 *
 * For `one-time` schedules the helper short-circuits to true: one-time has
 * its own validation flow via `executionDate` and the modal-level
 * "10 minutes in the future" rule, so this cross-field check does not apply.
 *
 * Returns true also for missing/invalid inputs so it stays safe inside a
 * react-hook-form validate function -- the other validators (required,
 * "End >= Start", `rangeIncludes*`, `rangeAllowsAtLeastOneCycle`) cover those
 * scenarios upstream and produce more specific error messages.
 *
 * @param {object} params
 * @param {string} params.frequency 'one-time' | 'daily' | 'weekly' | 'monthly'
 * @param {string} params.startDate MM/DD/YYYY
 * @param {string} params.endDate   MM/DD/YYYY
 * @param {string} params.executionTime24h 'HH:mm'
 * @param {string|number} [params.dayOfWeek] 0..6 (weekly only)
 * @param {string|number} [params.dayOfMonth] 1..31 or -1 for last day (monthly only)
 * @param {boolean} [params.isEditMode] use next-execution helper when true
 * @param {moment.Moment} [params.now] injectable "current time" (defaults to moment())
 * @returns {boolean}
 */
export const rangeAllowsAnyExecutionFromNow = ({
  frequency,
  startDate,
  endDate,
  executionTime24h,
  dayOfWeek,
  dayOfMonth,
  isEditMode = false,
  now = moment(),
}) => {
  // One-time has its own validation flow (executionDate + modal-level rules).
  // Return true here so the recurring-only check never produces a misleading
  // error on a one-time schedule.
  if (frequency === 'one-time') return true;
  if (!startDate || !endDate) return true;
  const start = moment(startDate, DATE_FORMAT, true);
  const end = moment(endDate, DATE_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return true;

  const firstExec = isEditMode
    ? computeNextExecutionDateTimeFromNow({
        frequency,
        startDate,
        executionTime24h,
        dayOfWeek,
        dayOfMonth,
        now,
      })
    : computeFirstExecutionDateTime({
        frequency,
        startDate,
        executionTime24h,
        dayOfWeek,
        dayOfMonth,
      });

  // Time helper could not resolve a first execution (invalid inputs etc.).
  // Other validators already surface the underlying issue, so do not produce
  // a misleading "no execution in window" error here.
  if (!firstExec) return true;

  return firstExec.isSameOrBefore(end.clone().endOf('day'));
};
